/**
 * Application constants and enums
 */

// API Error Codes
export enum ErrorCode {
    NETWORK_ERROR = 'NETWORK_ERROR',
    TIMEOUT = 'TIMEOUT',
    RATE_LIMIT = 'RATE_LIMIT',
    NOT_FOUND = 'NOT_FOUND',
    SERVER_ERROR = 'SERVER_ERROR',
    PARSE_ERROR = 'PARSE_ERROR',
    UNAUTHORIZED = 'UNAUTHORIZED',
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// HTTP Status Codes
export enum HttpStatus {
    OK = 200,
    BAD_REQUEST = 400,
    UNAUTHORIZED = 401,
    FORBIDDEN = 403,
    NOT_FOUND = 404,
    RATE_LIMITED = 429,
    SERVER_ERROR = 500,
    SERVICE_UNAVAILABLE = 503
}

// Cache TTLs (in seconds)
export const CACHE_TTL = {
    COUNTRIES: 86400,      // 24 hours
    STATES: 86400,         // 24 hours
    AGENCIES: 86400,       // 24 hours
    ROUTES: 3600,          // 1 hour
    STOPS: 3600,           // 1 hour
    ARRIVALS: 15,          // 15 seconds
    LIVE_STREAM: 5         // 5 seconds
} as const;

// Request timeouts (in milliseconds)
export const TIMEOUT = {
    DEFAULT: 12000,        // 12 seconds
    LONG: 20000,          // 20 seconds
    SHORT: 5000           // 5 seconds
} as const;

// Retry configuration
export const RETRY = {
    MAX_ATTEMPTS: 3,
    INITIAL_DELAY: 1000,   // 1 second
    MAX_DELAY: 10000,      // 10 seconds
    BACKOFF_MULTIPLIER: 2
} as const;

// Local storage keys
export const STORAGE_KEYS = {
    CONFIG: 'transit-hub-config',
    THEME: 'transit-hub-theme',
    DEVICE_ID: 'transit-hub-device-id'
} as const;

// Feature flags
export const FEATURES = {
    ENABLE_ANALYTICS: false,
    ENABLE_OFFLINE: true,
    ENABLE_SERVICE_WORKER: false,
    ENABLE_DEBUG_LOGS: false
} as const;

// UI Constants
export const UI = {
    REFRESH_INTERVAL: 30000,     // 30 seconds
    ALERT_ROTATION: 6000,        // 6 seconds (reduced from 8s)
    ANIMATION_DURATION: 300,     // 300ms
    DEBOUNCE_DELAY: 300,         // 300ms
    THROTTLE_DELAY: 1000,        // 1 second
    COUNTDOWN_UPDATE: 1000,      // Update countdown every second
    ACTIVATION_TOAST: 2500       // Toast notification duration
} as const;

// Arrival filtering constants
export const ARRIVALS = {
    MAX_MINUTES: 90,             // Maximum minutes to show regular arrivals
    EXTENDED_WINDOW: 2880,       // 48 hours for extended search (minutes)
    REALTIME_GRACE_PERIOD: -1,   // Real-time: remove at -1 min
    SCHEDULED_GRACE_PERIOD: -2   // Scheduled: remove at -2 min
} as const;

// Polling configuration (milliseconds)
export const POLLING = {
    MIN_INTERVAL: 30000,         // 30 seconds minimum
    MAX_INTERVAL: 300000,        // 5 minutes maximum
    DEFAULT_INTERVAL: 30000      // Initial interval
} as const;

// Geographic constants
export const GEO = {
    METERS_PER_DEGREE: 111000,          // Approximate meters per degree
    STOP_PROXIMITY_THRESHOLD: 50        // Meters to consider stops as duplicates
} as const;

// Allowed countries for transit lookup
export const ALLOWED_COUNTRIES = ['Canada', 'United States of America'] as const;

// Route types (GTFS spec)
export enum RouteType {
    TRAM = 0,
    SUBWAY = 1,
    RAIL = 2,
    BUS = 3,
    FERRY = 4,
    CABLE_TRAM = 5,
    AERIAL_LIFT = 6,
    FUNICULAR = 7,
    TROLLEYBUS = 11,
    MONORAIL = 12
}

// Arrival status
export type ArrivalStatus = 'On Time' | 'Delayed' | 'Early' | 'Cancelled';

// Live feed modes
export type LiveFeedMode = 'live' | 'connecting' | 'offline';

// Theme options
export type Theme = 'light' | 'dark';
